import { Link } from "@tanstack/react-router"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel"
import {
  ArrowLeft,
  MapPin,
  Radio,
  FileText,
  Navigation,
  Loader2,
  AlertCircle,
  Image as ImageIcon,
  Network,
} from "lucide-react"
import { useApiQuery } from "@/api/ApiQueryServiceProvider"
import type { GetSensingStationsResponse, GetNetworkSitesResponse, SensingStation } from "@/types/sensNet"
import { AppLogger } from "@/lib/logger"
import { ProfileSection } from "@/components/sensnet/ProfileLayout"

const log = AppLogger.getLogger(import.meta)

interface SensingStationDetailProps {
  stationNumber: number
}

function formatCoord(val: number | undefined | null, dir: 'lat' | 'lon') {
  if (val == null) return '—'
  const abs = Math.abs(val).toFixed(6)
  if (dir === 'lat') return `${abs}° ${val >= 0 ? 'N' : 'S'}`
  return `${abs}° ${val >= 0 ? 'E' : 'W'}`
}

function formatDate(ts: number | undefined) {
  if (!ts) return '—'
  return new Date(ts * 1000).toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
  })
}

export function SensingStationDetail({ stationNumber }: SensingStationDetailProps) {
  const { data, isLoading, isError, refetch } = useApiQuery<GetSensingStationsResponse>("getSensingStations", {})
  const { data: sitesData } = useApiQuery<GetNetworkSitesResponse>("getNetworkSites", {})

  const rawStations = (data as any)?.content?.stations || (data as any)?.stations || []
  const raw = rawStations.find((s: any) => s.stationNumber === stationNumber)

  const station: SensingStation | undefined = raw
    ? {
        id: raw.id ?? '',
        stationNumber: raw.stationNumber,
        name: raw.stationName || '',
        geoLocation: (raw.location || { latitude: 0, longitude: 0 }) as { latitude: number; longitude: number },
        addressIfAvailable: raw.stationAddress || '',
        stationRoadIntersections: raw.stationRoadIntersections || '',
        stationNotes: raw.stationNotes || '',
        coveredBySites: raw.coveredBySites || [],
        stationPictures: (raw.stationPictures || []).filter((p: unknown): p is string => typeof p === 'string'),
        createdAt: raw.createdAt ?? 0,
        updatedAt: raw.updatedAt ?? 0,
      }
    : undefined

  const networkSites = (sitesData as any)?.content?.networkSites || (sitesData as any)?.networkSites || []

  const coveredSites = networkSites.filter((site: any) =>
    station?.coveredBySites?.some((id) => String(id) === String(site.networkSiteNumber))
  )

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-4">
        <AlertCircle className="h-12 w-12 text-destructive" />
        <p className="text-destructive font-medium">Failed to load station data</p>
        <Button variant="outline" onClick={() => refetch()}>Retry</Button>
      </div>
    )
  }

  if (!station) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-4">
        <AlertCircle className="h-12 w-12 text-muted-foreground" />
        <p className="text-muted-foreground font-medium">Station #{stationNumber} not found</p>
        <Link to="/sensnet/sensing-stations">
          <Button variant="outline">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Stations
          </Button>
        </Link>
      </div>
    )
  }

  const googleMapsUrl = station.geoLocation?.latitude && station.geoLocation?.longitude
    ? `https://www.google.com/maps?q=${station.geoLocation.latitude},${station.geoLocation.longitude}`
    : null

  const mapEmbedUrl = station.geoLocation?.latitude && station.geoLocation?.longitude
    ? `https://www.openstreetmap.org/export/embed.html?bbox=${station.geoLocation.longitude - 0.01}%2C${station.geoLocation.latitude - 0.01}%2C${station.geoLocation.longitude + 0.01}%2C${station.geoLocation.latitude + 0.01}&layer=mapnik&marker=${station.geoLocation.latitude}%2C${station.geoLocation.longitude}`
    : null

  log.debug('Rendering station detail', { stationNumber, stationId: station.id })

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/sensnet/sensing-stations">
          <Button variant="outline" size="icon" className="h-9 w-9 shrink-0">
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </Link>
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
            <Radio className="h-5 w-5 text-primary" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl font-bold leading-tight truncate">{station.name}</h1>
            <p className="text-sm text-muted-foreground">
              Station #{station.stationNumber}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column — Location + Details */}
        <div className="lg:col-span-2 space-y-6">

          {/* Location Section */}
          <ProfileSection title="Location" icon={<MapPin className="h-4 w-4" />} variant="dark">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-5">
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Latitude</p>
                <p className="font-mono font-medium">{formatCoord(station.geoLocation?.latitude, 'lat')}</p>
              </div>
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Longitude</p>
                <p className="font-mono font-medium">{formatCoord(station.geoLocation?.longitude, 'lon')}</p>
              </div>
              {station.addressIfAvailable && (
                <div className="sm:col-span-2 space-y-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Address</p>
                  <p className="font-medium">{station.addressIfAvailable}</p>
                </div>
              )}
              {station.stationRoadIntersections && (
                <div className="sm:col-span-2 space-y-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Road Intersections</p>
                  <p className="font-medium">{station.stationRoadIntersections}</p>
                </div>
              )}
              {googleMapsUrl && (
                <div className="sm:col-span-2">
                  <a href={googleMapsUrl} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" size="sm" className="gap-2">
                      <Navigation className="h-3.5 w-3.5" />
                      Open in Google Maps
                    </Button>
                  </a>
                </div>
              )}
            </div>
          </ProfileSection>

          {/* Map Section */}
          <ProfileSection title="Map" icon={<Navigation className="h-4 w-4" />} variant="dark">
            {mapEmbedUrl ? (
              <iframe
                src={mapEmbedUrl}
                title="Station location map"
                className="w-full h-72 border-0"
                loading="lazy"
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-48 gap-2 text-muted-foreground">
                <MapPin className="h-8 w-8 opacity-30" />
                <p className="text-sm">No location coordinates available</p>
              </div>
            )}
          </ProfileSection>

          {/* Details Section */}
          <ProfileSection title="Details" icon={<FileText className="h-4 w-4" />} variant="amber">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-5">
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Station Number</p>
                <p className="font-mono font-medium">{station.stationNumber ?? '—'}</p>
              </div>
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Sites Covered By</p>
                <p className="font-medium">{station.coveredBySites?.length ?? 0}</p>
              </div>
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Created</p>
                <p className="font-medium">{formatDate(station.createdAt)}</p>
              </div>
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Last Updated</p>
                <p className="font-medium">{formatDate(station.updatedAt)}</p>
              </div>
              {station.stationNotes && (
                <div className="sm:col-span-2 space-y-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Notes</p>
                  <p className="text-sm leading-relaxed">{station.stationNotes}</p>
                </div>
              )}
            </div>
          </ProfileSection>
        </div>

        {/* Right column — Covered By Sites + Pictures */}
        <div className="space-y-6">

          {/* Covered By Sites */}
          <ProfileSection
            title={`Covered By Sites${coveredSites.length > 0 ? ` (${coveredSites.length})` : ''}`}
            icon={<Network className="h-4 w-4" />}
            variant="amber"
          >
            <div className="p-4">
              {coveredSites.length > 0 ? (
                <div className="space-y-3">
                  {coveredSites.map((site: any) => (
                    <div key={site.networkSiteNumber} className="flex items-start gap-3 p-3 rounded-lg border bg-muted/20 hover:bg-muted/40 transition-colors">
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary/10">
                        <Network className="h-4 w-4 text-primary" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-sm truncate">{(site as any).networkSiteName || site.name}</p>
                        {site.networkSiteNumber && (
                          <p className="text-xs text-muted-foreground font-mono">#{site.networkSiteNumber}</p>
                        )}
                        {site.siteAddress && (
                          <p className="text-xs text-muted-foreground truncate mt-0.5">{site.siteAddress}</p>
                        )}
                        <div className="flex items-center gap-1.5 mt-1">
                          <Badge variant="outline" className="text-xs">{String(site.indoorOrOutdoorSite ?? '')}</Badge>
                          <Badge variant="secondary" className="text-xs">{String(site.backhaulCapability ?? '')}</Badge>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-8 gap-2 text-muted-foreground">
                  <Network className="h-8 w-8 opacity-30" />
                  <p className="text-sm">Not covered by any sites</p>
                </div>
              )}
            </div>
          </ProfileSection>

          {/* Pictures Carousel */}
          <ProfileSection
            title={`Pictures${station.stationPictures?.length > 0 ? ` (${station.stationPictures.length})` : ''}`}
            icon={<ImageIcon className="h-4 w-4" />}
            variant="dark"
          >
            <div className="p-4">
              {station.stationPictures?.length > 0 ? (
                <Carousel className="w-full">
                  <CarouselContent>
                    {station.stationPictures.map((url, idx) => (
                      <CarouselItem key={idx}>
                        <div className="overflow-hidden rounded-lg border bg-muted aspect-video">
                          <img
                            src={url}
                            alt={`${station.name} — photo ${idx + 1}`}
                            className="h-full w-full object-cover"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none'
                              e.currentTarget.parentElement!.classList.add('flex', 'items-center', 'justify-center')
                            }}
                          />
                        </div>
                        <p className="text-xs text-center text-muted-foreground mt-2">
                          Photo {idx + 1} of {station.stationPictures.length}
                        </p>
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                  {station.stationPictures.length > 1 && (
                    <>
                      <CarouselPrevious className="left-1" />
                      <CarouselNext className="right-1" />
                    </>
                  )}
                </Carousel>
              ) : (
                <div className="flex flex-col items-center justify-center py-8 gap-2 text-muted-foreground">
                  <ImageIcon className="h-8 w-8 opacity-30" />
                  <p className="text-sm">No pictures available</p>
                </div>
              )}
            </div>
          </ProfileSection>
        </div>
      </div>
    </div>
  )
}
